/**
 * Sticky Add to Cart Bar
 * Shows a fixed bottom bar when the user scrolls past the main
 * product ATC button. Supports separate option selects per variant option,
 * bundle mode, price/image sync, and proper accessibility.
 *
 * Custom element: <sticky-atc>
 */

if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.mainProductInfo = null;
        this.mainProductForm = null;
        this.mainAtcButton = null;

        // Buttons (desktop + mobile)
        this.stickyBtns = this.querySelectorAll('.sticky-atc__submit');
        this.stickyImage = this.querySelector(`#StickyAtcImage-${this.sectionId}`);
        this.stickyPriceContainer = this.querySelector(`#StickyAtcPrice-${this.sectionId}`);

        // Separate option selects (desktop + mobile)
        this.optionSelects = this.querySelectorAll('.sticky-atc__option-select');

        // Variant data JSON
        this.variants = [];
        this._loadVariantData();

        this.isVisible = false;

        // Display trigger settings
        this.displayTrigger = this.dataset.displayTrigger || 'after_atc';
        this.scrollPercentage = parseInt(this.dataset.scrollPercentage, 10) || 30;

        this._onScroll = this._throttle(this._checkVisibility.bind(this), 100);
        this._onVariantChange = this._handleVariantChange.bind(this);
      }

      connectedCallback() {
        requestAnimationFrame(() => {
          this._findMainElements();
          this._bindEvents();
          this._renderVariantPrice(this._findVariantByOptions(this._getSelectedOptions()));
          this._addFooterSpacer();
          this.style.display = '';

          if (this.displayTrigger === 'always') {
            this.classList.add('is-visible');
            this.isVisible = true;
            this._updateFloatingElements(true);
          } else {
            this._checkVisibility();
          }
        });
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this._onScroll);
        document.removeEventListener('click',this._bundleSync);
        document.removeEventListener('change',this._bundleSync);
        if (this._variantChangeUnsub) {
          this._variantChangeUnsub();
          this._variantChangeUnsub = null;
        }
      }

      /* --- Initialization --- */

      _loadVariantData() {
        const jsonEl = document.getElementById(`StickyAtcVariantData-${this.sectionId}`);
        if (jsonEl) {
          try {
            this.variants = JSON.parse(jsonEl.textContent);
          } catch (e) {
            this.variants = [];
          }
        }
      }

      _findMainElements() {
        this.mainProductInfo =
          document.querySelector('product-info[data-section]') ||
          document.querySelector('.product__info-wrapper product-info') ||
          document.querySelector('product-info');

        if (this.mainProductInfo) {
          this.mainProductForm = this.mainProductInfo.querySelector('product-form');
        }
        if (!this.mainProductForm) {
          this.mainProductForm = document.querySelector('product-form');
        }

        if (this.mainProductForm) {
          this.mainAtcButton = this.mainProductForm.querySelector('[type="submit"]');
        }
        if (!this.mainAtcButton) {
          this.mainAtcButton = document.querySelector('[id^="ProductSubmitButton-"]');
        }
      }

      _bindEvents() {
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._bundleSync = event => { if(event.target.closest('.qb-tier')) requestAnimationFrame(()=>this._syncFromBundleTier()); };
        document.addEventListener('click',this._bundleSync);
        document.addEventListener('change',this._bundleSync);

        this.stickyBtns.forEach(btn => {
          btn.addEventListener('click', this._handleAddToCart.bind(this));
        });

        this.optionSelects.forEach(select => {
          select.addEventListener('change', this._handleOptionChange.bind(this));
        });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this._variantChangeUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
        }
      }

      _renderVariantPrice(variant) {
        if (!variant || !this.stickyPriceContainer) return;
        const money=value=>new Intl.NumberFormat('tr-TR',{minimumFractionDigits:2,maximumFractionDigits:2}).format(Number(value)/100)+' TL';
        const price=Number(variant.price),compare=Number(variant.compare_at_price)||0;
        const box=document.createElement('div');box.className='price'+(compare>price?' price--on-sale price--show-badge':'');
        const amount=document.createElement('span');amount.className='price-item price-item--sale';amount.textContent=money(price);box.append(amount);
        if(compare>price){const badge=document.createElement('span');badge.className='badge price__badge-sale';badge.textContent='-%'+Math.round((compare-price)*100/compare);box.append(badge);}
        this.stickyPriceContainer.replaceChildren(box);
      }

      _syncBundleTier(options) {
        const tiers=this.mainProductInfo?.querySelectorAll('.qb-tier');if(!tiers?.length)return;
        const quantity=parseInt(options[0],10);const tier=[...tiers].find(t=>Number(t.dataset.qty)===quantity);
        if(!tier)return;
        if(!tier.classList.contains('active'))tier.click();
        tier.querySelectorAll('.qb-iv-sel').forEach(sel=>{const value=options.find(v=>[...sel.options].some(o=>o.value===v));if(value&&sel.value!==value){sel.value=value;sel.dispatchEvent(new Event('change',{bubbles:true}));}});
      }

      _syncFromBundleTier() {
        this._findMainElements();const tier=this.mainProductInfo?.querySelector('.qb-tier.active');if(!tier)return;
        const quantity=Number(tier.dataset.qty)||1;
        const color=[...tier.querySelectorAll('.qb-iv-sel')].find(sel=>this.variants.some(v=>v.options?.[1]===sel.value))?.value;
        const options=this._getSelectedOptions();options[0]=quantity+' Adet';if(color)options[1]=color;
        const variant=this._findVariantByOptions(options);if(!variant)return;
        this.optionSelects.forEach(sel=>{sel.value=variant.options[Number(sel.dataset.optionIndex)];});
        this._renderVariantPrice(variant);
      }

      /* --- Scroll / Visibility --- */

      _checkVisibility() {
        let shouldShow = false;

        switch (this.displayTrigger) {
          case 'always':
            shouldShow = true;
            break;

          case 'after_percentage': {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
            shouldShow = scrollPercent >= this.scrollPercentage;
            break;
          }

          case 'after_atc':
          default: {
            if (!this.mainAtcButton) {
              this._findMainElements();
              if (!this.mainAtcButton) return;
            }
            const rect = this.mainAtcButton.getBoundingClientRect();
            shouldShow = rect.bottom < 0;
            break;
          }
        }

        if (shouldShow && !this.isVisible) {
          this.classList.add('is-visible');
          this.isVisible = true;
          this._updateFloatingElements(true);
        } else if (!shouldShow && this.isVisible) {
          this.classList.remove('is-visible');
          this.isVisible = false;
          this._updateFloatingElements(false);
        }
      }

      _updateFloatingElements(visible) {
        const offset = visible ? `${this.offsetHeight}px` : '0px';
        document.documentElement.style.setProperty('--sticky-atc-offset', offset);
      }

      /* --- Add to Cart --- */

      _handleAddToCart(e) {
        e.preventDefault();
        const btn = e.currentTarget;

        if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true') {
          return;
        }

        if (
          window.__erdensaiBundleMode === 'multi' &&
          Array.isArray(window.__erdensaiBundleItems) &&
          window.__erdensaiBundleItems.length > 0
        ) {
          this._submitViaMainForm(btn);
          return;
        }

        this._syncCurrentOptionsToMainForm();
        this._submitViaMainForm(btn);
      }

      _submitViaMainForm(clickedBtn) {
        if (!this.mainProductForm) {
          this._findMainElements();
        }

        if (this.mainProductForm) {
          // Show loading state on ALL sticky buttons (desktop + mobile)
          this.stickyBtns.forEach(b => {
            b.classList.add('loading');
            b.setAttribute('aria-disabled', 'true');
            const spinner = b.querySelector('.loading__spinner');
            if (spinner) spinner.classList.remove('hidden');
          });

          const form = this.mainProductForm.querySelector('form');
          if (form) {
            const submitEvent = new SubmitEvent('submit', { cancelable: true, bubbles: true });
            form.dispatchEvent(submitEvent);
          }

          const resetLoading = () => {
            this.stickyBtns.forEach(b => {
              b.classList.remove('loading');
              b.removeAttribute('aria-disabled');
              const spinner = b.querySelector('.loading__spinner');
              if (spinner) spinner.classList.add('hidden');
            });
          };

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const unsubUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
              resetLoading();
              unsubUpdate();
              unsubError();
            });
            const unsubError = subscribe(PUB_SUB_EVENTS.cartError, () => {
              resetLoading();
              unsubUpdate();
              unsubError();
            });
            setTimeout(resetLoading, 8000);
          } else {
            setTimeout(resetLoading, 3000);
          }
        }
      }

      /* --- Option / Variant Handling --- */

      _handleOptionChange(e) {
        const changedSelect = e.target;
        const optionIndex = parseInt(changedSelect.dataset.optionIndex, 10);
        const newValue = changedSelect.value;

        // Sync same option index across desktop + mobile selects
        this.optionSelects.forEach(sel => {
          if (sel !== changedSelect && parseInt(sel.dataset.optionIndex, 10) === optionIndex) {
            sel.value = newValue;
          }
        });

        const selectedOptions = this._getSelectedOptions();
        const matchingVariant = this._findVariantByOptions(selectedOptions);

        if (matchingVariant) {
          this._syncVariantIdToMainForm(matchingVariant.id);
          this._updateButtonStateFromVariant(matchingVariant);
          this._syncToMainVariantSelects(matchingVariant, selectedOptions);
          this._syncBundleTier(selectedOptions);
          this._renderVariantPrice(matchingVariant);
        } else {
          this._setAllButtonsState(true, window.variantStrings?.unavailable || 'Unavailable');
        }
      }

      _getSelectedOptions() {
        const options = {};
        this.optionSelects.forEach(sel => {
          const idx = sel.dataset.optionIndex;
          if (!(idx in options)) {
            options[idx] = sel.value;
          }
        });
        return Object.keys(options).sort().map(k => options[k]);
      }

      _findVariantByOptions(selectedOptions) {
        return this.variants.find(variant => {
          return selectedOptions.every((val, idx) => {
            return variant.options && variant.options[idx] === val;
          });
        });
      }

      _syncCurrentOptionsToMainForm() {
        const selectedOptions = this._getSelectedOptions();
        const matchingVariant = this._findVariantByOptions(selectedOptions);
        if (matchingVariant) {
          this._syncVariantIdToMainForm(matchingVariant.id);
        }
      }

      _syncVariantIdToMainForm(variantId) {
        if (!this.mainProductInfo) return;

        const sectionAttr = this.mainProductInfo.dataset.section;
        const forms = document.querySelectorAll(
          `#product-form-${sectionAttr}, #product-form-installment-${sectionAttr}`
        );

        forms.forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (input) {
            input.value = variantId;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      _syncToMainVariantSelects(variant, selectedOptions) {
        const mainSection = this.mainProductInfo;
        if (!mainSection) return;

        const variantSelects = mainSection.querySelector('variant-selects');
        if (!variantSelects) return;

        // Sync main dropdowns
        const mainSelectEls = variantSelects.querySelectorAll('select');
        mainSelectEls.forEach((sel, idx) => {
          if (selectedOptions[idx] !== undefined) {
            sel.value = selectedOptions[idx];
          }
        });

        // Sync main radio/swatch inputs
        const fieldsets = variantSelects.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset, idx) => {
          if (selectedOptions[idx] !== undefined) {
            const inputs = fieldset.querySelectorAll('input[type="radio"]');
            inputs.forEach(input => {
              if (input.value === selectedOptions[idx]) {
                input.checked = true;
              }
            });
          }
        });

        // Dispatch change to trigger Dawn variant logic
        variantSelects.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* --- Variant Change Handler (from main product) --- */

      _handleVariantChange(event) {
        const { data } = event;
        if (!data) return;

        const { variant, html, sectionId } = data;

        // Sync sticky option selects
        if (variant && variant.options) {
          this.optionSelects.forEach(sel => {
            const idx = parseInt(sel.dataset.optionIndex, 10);
            if (variant.options[idx] !== undefined) {
              sel.value = variant.options[idx];
            }
          });
        }

        // Update price
        if (variant) { this._renderVariantPrice(variant); } else if (html && this.stickyPriceContainer) { this._updatePriceFromHtml(html); }

        // Update image
        if (variant && variant.featured_image && this.stickyImage) {
          this.stickyImage.src = variant.featured_image.src.replace(/(\.[^.]+)$/, '_100x$1');
          this.stickyImage.srcset = '';
        } else if (html && this.stickyImage) {
          this._updateImageFromHtml(html);
        }

        // Update buttons
        this._updateButtonStateFromVariant(variant);
      }

      _updatePriceFromHtml(html) {
        if (!this.stickyPriceContainer) return;

        let doc = html;
        if (typeof html === 'string') {
          doc = new DOMParser().parseFromString(html, 'text/html');
        }

        const mainSectionId = this.mainProductInfo?.dataset.section || '';
        const sourcePrice =
          doc.getElementById(`price-${mainSectionId}`) ||
          doc.querySelector('.price');

        if (sourcePrice) {
          const destPrice = this.stickyPriceContainer.querySelector('.price');
          if (destPrice) {
            destPrice.innerHTML = sourcePrice.innerHTML;
            destPrice.className = sourcePrice.className;
          }
        }
      }

      _updateImageFromHtml(html) {
        if (!this.stickyImage) return;

        let doc = html;
        if (typeof html === 'string') {
          doc = new DOMParser().parseFromString(html, 'text/html');
        }

        const mediaGallery = doc.querySelector('media-gallery');
        if (mediaGallery) {
          const firstImg = mediaGallery.querySelector('img');
          if (firstImg) {
            this.stickyImage.src = firstImg.src.replace(/&width=\d+/, '&width=100');
          }
        }
      }

      _updateButtonStateFromVariant(variant) {
        if (!variant) {
          this._setAllButtonsState(true, window.variantStrings?.unavailable || 'Unavailable');
        } else if (!variant.available) {
          this._setAllButtonsState(true, window.variantStrings?.soldOut || 'Sold out');
        } else {
          this._setAllButtonsState(false, window.variantStrings?.addToCart || 'Add to cart');
        }
      }

      _setAllButtonsState(disabled, text) {
        this.stickyBtns.forEach(btn => {
          const btnText = btn.querySelector('.sticky-atc__btn-text');
          if (disabled) {
            btn.setAttribute('disabled', '');
            btn.setAttribute('aria-disabled', 'true');
          } else {
            btn.removeAttribute('disabled');
            btn.removeAttribute('aria-disabled');
          }
          if (btnText && text) {
            btnText.textContent = text;
          }
        });
      }

      /* --- Footer Spacer --- */

      _addFooterSpacer() {
        if (document.querySelector('.sticky-atc-footer-spacer')) return;

        const spacer = document.createElement('div');
        spacer.className = 'sticky-atc-footer-spacer';
        spacer.style.height = `${this.offsetHeight}px`;

        const footer = document.querySelector('footer') || document.querySelector('[class*="footer"]');
        if (footer) {
          const footerBg = window.getComputedStyle(footer).backgroundColor;
          spacer.style.backgroundColor = footerBg;
        }

        document.body.appendChild(spacer);
      }

      /* --- Utilities --- */

      _throttle(fn, delay) {
        let lastCall = 0;
        let timer = null;
        return function (...args) {
          const now = Date.now();
          const remaining = delay - (now - lastCall);
          if (remaining <= 0) {
            if (timer) {
              clearTimeout(timer);
              timer = null;
            }
            lastCall = now;
            fn.apply(this, args);
          } else if (!timer) {
            timer = setTimeout(() => {
              lastCall = Date.now();
              timer = null;
              fn.apply(this, args);
            }, remaining);
          }
        };
      }
    }
  );
}